const GOALS = [
  { v: "debt_free_sooner", l: "Become debt-free sooner" },
  { v: "max_interest", l: "Save maximum interest" },
  { v: "lower_emi", l: "Lower my monthly EMI" },
  { v: "liquidity", l: "Keep money liquid / flexible" },
];
const SOURCES = ["Bonus", "Incentive", "FD maturity", "Asset sale", "Gift", "Other"];
const EF = [{ v: "0", l: "0" }, { v: "1-3", l: "1-3" }, { v: "3-6", l: "3-6" }, { v: "6+", l: "6+" }];

export const DEFAULT_ANSWERS = {
  goal: "max_interest", lump_sum: 0, lump_source: "Bonus", extra_monthly: 0, emergency_fund: "3-6",
  big_expense: false, big_expense_amount: 0, tax_regime: null, expected_return_pct: null, risk_comfort: null,
};

function Chips({ value, options, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} aria-pressed={value === o.v}
                className={`px-2.5 py-1 text-[11px] rounded border transition-colors duration-150 ${value === o.v ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "hairline text-muted-foreground hover:text-foreground"}`}
                data-testid={`${testid}-${o.v}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="eyebrow text-[9px] mb-1">{label}</div>{children}</label>;
}

export function AiQuestionnaire({ a, set, models, modelId, setModelId, remaining, limit, onSubmit, busy }) {
  const num = (k) => (e) => set({ [k]: Math.max(0, Number(e.target.value) || 0) });
  return (
    <div className="card-flat p-4 sm:p-5 space-y-4" data-testid="ai-questionnaire">
      <Field label="Main goal"><Chips value={a.goal} options={GOALS} onChange={(v) => set({ goal: v })} testid="ai-goal" /></Field>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Money available now (₹, optional)">
          <input type="number" min="0" value={a.lump_sum || ""} onChange={num("lump_sum")} className="input-dark w-full px-2 py-1.5 text-sm" data-testid="ai-lump-sum" />
        </Field>
        <Field label="Source">
          <select value={a.lump_source} onChange={(e) => set({ lump_source: e.target.value })} className="input-dark w-full px-2 py-1.5 text-xs" data-testid="ai-lump-source">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Extra I can pay every month (₹, optional)">
          <input type="number" min="0" value={a.extra_monthly || ""} onChange={num("extra_monthly")} className="input-dark w-full px-2 py-1.5 text-sm" data-testid="ai-extra-monthly" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Emergency fund I already have (months of expenses)">
          <Chips value={a.emergency_fund} options={EF} onChange={(v) => set({ emergency_fund: v })} testid="ai-ef" />
        </Field>
        <Field label="Big expense expected in the next 12 months?">
          <div className="flex items-center gap-2 flex-wrap">
            <Chips value={a.big_expense ? "yes" : "no"} options={[{ v: "no", l: "No" }, { v: "yes", l: "Yes" }]}
                   onChange={(v) => set({ big_expense: v === "yes" })} testid="ai-big-expense" />
            {a.big_expense && (
              <input type="number" min="0" placeholder="Amount ₹" value={a.big_expense_amount || ""} onChange={num("big_expense_amount")}
                     className="input-dark px-2 py-1 text-xs w-32" data-testid="ai-big-expense-amount" />
            )}
          </div>
        </Field>
      </div>
      <details className="text-xs" data-testid="ai-optional">
        <summary className="cursor-pointer text-muted-foreground">Optional: tax regime, expected return, risk comfort</summary>
        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <Field label="Tax regime">
            <Chips value={a.tax_regime} options={[{ v: "Old", l: "Old" }, { v: "New", l: "New" }]} onChange={(v) => set({ tax_regime: v })} testid="ai-tax" />
          </Field>
          <Field label="Expected post-tax return on investments (%)">
            <input type="number" min="0" max="50" step="0.5" value={a.expected_return_pct ?? ""}
                   onChange={(e) => set({ expected_return_pct: e.target.value === "" ? null : Number(e.target.value) })}
                   className="input-dark w-full px-2 py-1.5 text-sm" data-testid="ai-return" />
          </Field>
          <Field label="Comfort with risk">
            <Chips value={a.risk_comfort} options={[{ v: "Low", l: "Low" }, { v: "Medium", l: "Medium" }, { v: "High", l: "High" }]} onChange={(v) => set({ risk_comfort: v })} testid="ai-risk" />
          </Field>
        </div>
      </details>
      <div className="flex flex-wrap items-end gap-3 border-t hairline pt-3">
        <Field label="AI model">
          <select value={modelId} onChange={(e) => setModelId(e.target.value)} className="input-dark px-2 py-1.5 text-xs" data-testid="ai-model-select">
            {models.map((m) => <option key={m.id} value={m.id}>{m.label}{m.available ? "" : " (not set up)"}</option>)}
          </select>
        </Field>
        <div className="text-[11px] text-muted-foreground" data-testid="ai-remaining">{remaining} of {limit} AI suggestions left today</div>
        <button type="button" onClick={onSubmit} disabled={busy || remaining <= 0}
                className="btn-primary px-4 py-2 text-sm ml-auto disabled:opacity-40" data-testid="ai-generate-btn">
          {busy ? "Thinking… (up to 20s)" : "Get AI suggestion"}
        </button>
      </div>
    </div>
  );
}
